import { Component, Prop, State, h, Listen, Element } from '@stencil/core';

interface ProductVariant {
  title?: string;
  id?: string;
  price?: number;
  available?: boolean;
  image?: string;
  link?: string;
  color?: string;
  size?: string;
}

interface ProductSummary {
  title?: string;
  product_id?: string;
  min_price?: number;
  max_price?: number;
  product_url?: string;
  product_image?: string;
  variants?: ProductVariant[];
  content?: string;
  available?: boolean;
  type?: string;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  products?: ProductSummary[];
}

@Component({
  tag: 'textyess-dialog-trigger-widget',
  styleUrl: 'textyess-dialog-trigger-widget.css',
  shadow: true,
})
export class TextyessDialogTriggerWidget {
  @Element() hostElement: HTMLElement;

  @Prop({ attribute: 'organization_id' }) organizationId: string;
  @Prop({ attribute: 'background_color' }) backgroundColor: string = '#000000';
  @Prop({ attribute: 'icon_color' }) iconColor: string = '#ffffff';
  @Prop({ attribute: 'tooltip_text' }) tooltipText: string;

  @State() isDialogOpen = false;
  @State() messages: ChatMessage[] = [];
  @State() inputValue: string = '';
  @State() isSending = false;
  @State() errorMessage: string;

  private tooltipId = `textyess-dialog-trigger-tooltip-${Math.random().toString(36).slice(2)}`;
  private dialogTitleId = `textyess-dialog-title-${Math.random().toString(36).slice(2)}`;
  private messageListRef?: HTMLDivElement;
  private promptInputRef?: HTMLTextAreaElement;
  private previousBodyOverflow?: string;
  private conversationId?: string;

  private defaultAssistantGreeting = 'Hi! I\'m Textyess AI. How can I help you today?';

  private handleOpenDialog = () => {
    this.isDialogOpen = true;
    this.lockBodyScroll();

    if (!this.messages.length) {
      this.messages = [
        {
          id: this.generateMessageId(),
          role: 'assistant',
          content: this.defaultAssistantGreeting,
        },
      ];
    }

    requestAnimationFrame(() => {
      this.promptInputRef?.focus();
      if (this.promptInputRef) {
        this.adjustTextareaHeight(this.promptInputRef);
      }
      this.queueScrollToBottom();
    });
  };

  private handleCloseDialog = () => {
    this.isDialogOpen = false;
    this.unlockBodyScroll();
  };

  private handleBackdropClick = (event: MouseEvent) => {
    if (event.target === event.currentTarget) {
      this.handleCloseDialog();
    }
  };

  disconnectedCallback() {
    this.unlockBodyScroll();
  }

  @Listen('keydown', { target: 'window' })
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.isDialogOpen) {
      this.handleCloseDialog();
    }
  }

  private lockBodyScroll() {
    if (typeof document === 'undefined') {
      return;
    }
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  private unlockBodyScroll() {
    if (typeof document === 'undefined') {
      return;
    }
    if (this.previousBodyOverflow !== undefined) {
      document.body.style.overflow = this.previousBodyOverflow;
      this.previousBodyOverflow = undefined;
    } else {
      document.body.style.removeProperty('overflow');
    }
  }

  private generateMessageId(): string {
    return `msg-${Math.random().toString(36).slice(2, 10)}`;
  }

  private ensureConversationId(): string {
    if (!this.conversationId) {
      const suffix = Math.random().toString(36).slice(2, 8);
      this.conversationId = this.organizationId
        ? `${this.organizationId}-${suffix}`
        : `conversation-${suffix}`;
    }
    return this.conversationId;
  }

  private getSystemPrompt(): string {
    const globalPrompt = typeof window !== 'undefined' ? (window as any)?.textyessWidgetBusinessRules : undefined;
    if (typeof globalPrompt === 'string' && globalPrompt.trim().length > 0) {
      return globalPrompt;
    }
    return '';
  }

  private getLanguage(): string {
    const globalLanguage = typeof window !== 'undefined' ? (window as any)?.textyessWidgetLanguage : undefined;
    if (typeof globalLanguage === 'string' && globalLanguage.trim().length > 0) {
      return globalLanguage;
    }
    return 'en';
  }

  private getChatEndpoint(): string {
    if (typeof window !== 'undefined') {
      const globalEndpoint = (window as any)?.textyessChatEndpoint;
      if (typeof globalEndpoint === 'string' && globalEndpoint.trim().length > 0) {
        return globalEndpoint;
      }
    }
    return '/api/chat';
  }

  private adjustTextareaHeight(textarea: HTMLTextAreaElement) {
    textarea.style.height = 'auto';
    const nextHeight = Math.min(textarea.scrollHeight, 160);
    textarea.style.height = `${nextHeight}px`;
  }

  private handleInputChange = (event: Event) => {
    const target = event.target as HTMLTextAreaElement;
    this.inputValue = target.value;
    this.adjustTextareaHeight(target);
  };

  private handlePromptKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  };

  private async sendMessage(event?: Event) {
    event?.preventDefault();

    const trimmedValue = this.inputValue.trim();
    if (!trimmedValue || this.isSending) {
      return;
    }

    const userMessage: ChatMessage = {
      id: this.generateMessageId(),
      role: 'user',
      content: trimmedValue,
    };

    const assistantMessage: ChatMessage = {
      id: this.generateMessageId(),
      role: 'assistant',
      content: '',
      products: [],
    };

    this.messages = [...this.messages, userMessage, assistantMessage];
    this.errorMessage = undefined;
    this.inputValue = '';
    if (this.promptInputRef) {
      this.promptInputRef.value = '';
      this.adjustTextareaHeight(this.promptInputRef);
    }
    this.queueScrollToBottom();

    this.isSending = true;

    try {
      await this.streamAssistantResponse(trimmedValue, assistantMessage.id);
    } catch (error) {
      const fallbackMessage = error instanceof Error ? error.message : 'An unexpected error occurred.';
      this.errorMessage = fallbackMessage;
      this.updateAssistantMessage(assistantMessage.id, `Sorry, I encountered an error: ${fallbackMessage}`, []);
    } finally {
      this.isSending = false;
      this.queueScrollToBottom();
    }
  }

  private async streamAssistantResponse(userInput: string, assistantMessageId: string) {
    const endpoint = this.getChatEndpoint();

    const payload = {
      messages: [
        ...(this.getSystemPrompt()
          ? [{ role: 'system', content: this.getSystemPrompt() }]
          : []),
        { role: 'user', content: userInput },
      ],
      stream: true,
      model: 'gpt-4o',
      max_products: 5,
      conversation_id: this.ensureConversationId(),
      language: this.getLanguage(),
      business_rules: this.getSystemPrompt(),
      organization_id: this.organizationId,
    };

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`);
    }

    if (!response.body) {
      const fallback = await response.text();
      if (fallback) {
        this.updateAssistantMessage(assistantMessageId, fallback, []);
      }
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let assistantContent = '';
    let productPayload: ProductSummary[] = [];

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      const chunk = decoder.decode(value, { stream: true });
      if (!chunk) {
        continue;
      }

      const lines = chunk.split('\n');
      let hasProcessedSSE = false;

      for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line || !line.startsWith('data:')) {
          continue;
        }

        const data = line.slice(5).trim();
        if (!data || data === '[DONE]') {
          continue;
        }

        hasProcessedSSE = true;

        try {
          const parsed = JSON.parse(data);
          const deltaContent = parsed?.choices?.[0]?.delta?.content;
          if (typeof deltaContent === 'string') {
            assistantContent += deltaContent;
          }
          if (Array.isArray(parsed?.products)) {
            productPayload = parsed.products as ProductSummary[];
          }
        } catch (parseError) {
          continue;
        }
      }

      if (!hasProcessedSSE) {
        assistantContent += chunk;
      }

      this.updateAssistantMessage(assistantMessageId, assistantContent, productPayload);
      this.queueScrollToBottom();
    }

    this.updateAssistantMessage(assistantMessageId, assistantContent, productPayload);
  }

  private updateAssistantMessage(messageId: string, content: string, products?: ProductSummary[]) {
    this.messages = this.messages.map(message => {
      if (message.id !== messageId) {
        return message;
      }

      return {
        ...message,
        content,
        products: products ?? message.products,
      };
    });
  }

  private queueScrollToBottom() {
    if (!this.messageListRef) {
      return;
    }
    requestAnimationFrame(() => {
      if (this.messageListRef) {
        this.messageListRef.scrollTop = this.messageListRef.scrollHeight;
      }
    });
  }

  private renderProductCarousel(products?: ProductSummary[]) {
    if (!products || !products.length) {
      return null;
    }

    const validProducts = products.filter(product => Boolean(product?.title));
    if (!validProducts.length) {
      return null;
    }

    return (
      <div class="product-carousel" role="group" aria-label="Product suggestions">
        <div class="product-carousel-track">
          {validProducts.map(product => this.renderProductCard(product))}
        </div>
      </div>
    );
  }

  private renderProductCard(product: ProductSummary) {
    const fallbackImage = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQwIiBoZWlnaHQ9IjI0MCIgdmlld0JveD0iMCAwIDI0MCAyNDAiIGZpbGw9IiNFQ0VDRUQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjI0MCIgaGVpZ2h0PSIyNDAiIHJ4PSI0MCIgZmlsbD0iI0ZGZiIvPjxwYXRoIGQ9Ik03MCAxMzJMMTAxLjUgMTU0TDE1MCA5MCIgc3Ryb2tlPSIjQkNCM0JBIiBzdHJva2Utd2lkdGg9IjE4IiBzdHJva2UtbGluZWNhcD0icm91bmQiIGZpbGw9Im5vbmUiIHN0cm9rZS1saW5lam9pbj0icm91bmQiLz48Y2lyY2xlIGN4PSIxNzAiIGN5PSI4MCIgcj0iMTUiIGZpbGw9IiNCQ0IzQkEiLz48L3N2Zz4=';

    const formatPrice = (price?: number) => {
      if (price === null || price === undefined || Number.isNaN(price)) {
        return null;
      }
      return `€${Number(price).toFixed(2)}`;
    };

    const priceLabel = (() => {
      const min = formatPrice(product.min_price);
      const max = formatPrice(product.max_price);
      if (min && max && min !== max) {
        return `${min} - ${max}`;
      }
      return min || max;
    })();

    return (
      <article class="product-card" key={product.product_id || product.title}>
        <div class="product-image-wrapper">
          <img
            src={product.product_image || fallbackImage}
            alt={product.title || 'Product suggestion'}
            onError={(event: Event) => {
              const target = event.target as HTMLImageElement;
              target.src = fallbackImage;
            }}
          />
        </div>
        <div class="product-card-body">
          <p class="product-card-title">{product.title}</p>
          {priceLabel && <p class="product-card-price">{priceLabel}</p>}
          {product.product_url && (
            <a class="product-card-link" href={product.product_url} target="_blank" rel="noopener noreferrer">
              View product
            </a>
          )}
        </div>
      </article>
    );
  }

  private renderMessages() {
    if (this.messages.length === 0) {
      return (
        <div class="empty-conversation" role="status">
          <p class="empty-conversation-title">Start a conversation</p>
          <p class="empty-conversation-subtitle">Ask about products, availability, or styling advice.</p>
        </div>
      );
    }

    return this.messages.map(message => {
      const isUser = message.role === 'user';
      const hasContent = message.content && message.content.trim().length > 0;

      return (
        <div class={{ 'message-row': true, 'message-row-user': isUser, 'message-row-assistant': !isUser }} key={message.id}>
          <div class={{ 'message-bubble': true, 'message-bubble-user': isUser, 'message-bubble-assistant': !isUser }}>
            <p class="message-text">{hasContent ? message.content : '...'}</p>
            {!hasContent && !isUser && this.isSending && (
              <div class="typing-indicator" aria-label="Assistant is typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            )}
            {!isUser && this.renderProductCarousel(message.products)}
          </div>
        </div>
      );
    });
  }

  render() {
    const tooltipId = this.tooltipText ? this.tooltipId : undefined;

    return (
      <div class="trigger-container" data-organization-id={this.organizationId || null}>
        <button
          type="button"
          class="trigger-button"
          style={{ backgroundColor: this.backgroundColor, color: this.iconColor }}
          onClick={this.handleOpenDialog}
          aria-haspopup="dialog"
          aria-expanded={this.isDialogOpen ? 'true' : 'false'}
          aria-label={this.tooltipText || 'Open dialog'}
          aria-describedby={tooltipId}
        >
          <span class="trigger-icon" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="3" width="18" height="18" rx="3" ry="3" fill="none" stroke="currentColor" stroke-width="2" />
              <path d="M12 7V17" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              <path d="M7 12H17" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          </span>
        </button>
        {this.tooltipText && (
          <div id={tooltipId} class="tooltip" role="tooltip">
            {this.tooltipText}
          </div>
        )}

        {this.isDialogOpen && (
          <div class="dialog-backdrop" onClick={this.handleBackdropClick}>
            <div class="dialog" role="dialog" aria-modal="true" aria-labelledby={this.dialogTitleId}>
              <header class="dialog-header">
                <div class="dialog-header-text">
                  <p class="dialog-pill">AI Assistant</p>
                  <h2 id={this.dialogTitleId}>Shop with Textyess</h2>
                  <p class="dialog-subtitle">Chat with our AI stylist and get product suggestions instantly.</p>
                </div>
                <button type="button" class="close-button" onClick={this.handleCloseDialog} aria-label="Close dialog">
                  ×
                </button>
              </header>

              <div class="dialog-body">
                <div class="message-container" ref={el => (this.messageListRef = el)}>
                  {this.renderMessages()}
                  {this.errorMessage && (
                    <div class="error-banner" role="alert">{this.errorMessage}</div>
                  )}
                </div>
                <form class="prompt-form" onSubmit={event => this.sendMessage(event)}>
                  <label class="sr-only" htmlFor="textyess-chat-input">
                    Message
                  </label>
                  <textarea
                    id="textyess-chat-input"
                    rows={1}
                    ref={el => (this.promptInputRef = el)}
                    class="prompt-textarea"
                    placeholder="Ask about products, sizes, or availability..."
                    value={this.inputValue}
                    onInput={this.handleInputChange}
                    onKeyDown={event => this.handlePromptKeyDown(event)}
                  ></textarea>
                  <div class="prompt-actions">
                    <button
                      type="submit"
                      class="prompt-submit"
                      disabled={!this.inputValue.trim() || this.isSending}
                    >
                      {this.isSending ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}
